// Google Ads measurement: whether our own ads lead to a bot being built and a
// plan being bought.
//
// Only on buildbotsnow.com itself, on our public pages and the dashboard. The
// chat widget is a separate script that runs on customers' sites, and this
// file is never part of it: the privacy policy promises the widget sets no
// cookies, and an advertising tag there would also track their visitors.
//
// The tag is added from here rather than as an inline <script> in index.html,
// because the site's content security policy allows inline scripts only by
// hash; firebase.json lists Google's domains for it instead. The ids are not
// secret (they are in every page's source once the tag loads); a copy lives in
// RTDB /config/googleAds.
//
// Each conversion is reported once: Google drops a repeat of the same
// transaction_id, and a paid plan is also remembered in this browser, since the
// page it is reported from can be reloaded.

export const ADS_TAG_ID = "AW-18511871617";

export const CONVERSIONS = {
  botBuilt: "AW-18511871617/eqqlCO-KiZwdEIH9kvtE",
  paidPlan: "AW-18511871617/z6vcCPKKiZwdEIH9kvtE"
} as const;

const SITE_HOSTS = ["buildbotsnow.com", "www.buildbotsnow.com"];
const REPORTED_KEY = "buildbotsnow.adsReported";

type Gtag = (...args: unknown[]) => void;
type AdsWindow = Window & { dataLayer?: unknown[]; gtag?: Gtag };

export function adsEnabled(hostname: string = window.location.hostname): boolean {
  return SITE_HOSTS.includes(hostname);
}

// Adds Google's tag to the page, once. Returns whether it is on the page.
export function loadAdsTag({ hostname, win = window as AdsWindow }: { hostname?: string; win?: AdsWindow } = {}): boolean {
  if (!adsEnabled(hostname ?? win.location.hostname)) return false;
  if (win.gtag) return true;
  win.dataLayer = win.dataLayer || [];
  // Google's own definition: the tag reads each call's `arguments` object, so
  // this cannot be an arrow function or take a rest parameter.
  win.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    win.dataLayer!.push(arguments);
  } as Gtag;
  win.gtag("js", new Date());
  // allow_enhanced_conversions: the sign-up email, which the tag hashes in the
  // browser before sending, matches conversions whose ad cookie was blocked.
  win.gtag("config", ADS_TAG_ID, { allow_enhanced_conversions: true });
  const script = win.document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${ADS_TAG_ID}`;
  win.document.head.appendChild(script);
  return true;
}

function reported(win: AdsWindow): string[] {
  try {
    const value = JSON.parse(win.localStorage.getItem(REPORTED_KEY) || "[]");
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}

function remember(win: AdsWindow, id: string) {
  try {
    win.localStorage.setItem(REPORTED_KEY, JSON.stringify([...reported(win), id].slice(-50)));
  } catch {
    // Private mode: Google's own transaction_id check still stops a repeat.
  }
}

// A bot was built: the wizard's request succeeded. One per bot.
export function reportBotBuilt({ agentId, email, win = window as AdsWindow }: { agentId: string; email?: string | null; win?: AdsWindow }): boolean {
  if (!win.gtag) return false;
  if (email) win.gtag("set", "user_data", { email });
  win.gtag("event", "conversion", { send_to: CONVERSIONS.botBuilt, transaction_id: agentId });
  return true;
}

// A paid plan was bought: back from Stripe checkout, with the subscription
// landed. Once per bot and plan, with the plan's monthly price as its value.
export function reportPaidPlan({
  agentId,
  plan,
  valueUsd,
  win = window as AdsWindow
}: {
  agentId: string;
  plan: string;
  valueUsd: number;
  win?: AdsWindow;
}): boolean {
  if (!win.gtag) return false;
  const id = `${agentId}:${plan}`;
  if (reported(win).includes(id)) return false;
  win.gtag("event", "conversion", { send_to: CONVERSIONS.paidPlan, value: valueUsd, currency: "USD", transaction_id: id });
  remember(win, id);
  return true;
}
